import React, { useEffect, useMemo, useState } from "react";
import { Drawer, DialogTitle, DialogContent, DialogActions, Button, Box, Tabs, Tab, Typography, CircularProgress, ToggleButton, ToggleButtonGroup, FormControlLabel, Checkbox, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "../../../react-query";
import { formatExposure, formatPercent } from "../../../utils/formatters";
import { riskLimitsApi } from "../../../services/api";
import { useRiskLimitsUtilizationDetail, useRiskLimitsStrategyVar, useRiskLimitsInvestmentVar, useRiskLimitsGreeksDetail, useRiskLimitsPmApproval } from "../hooks/useRiskLimitsData";
import { isWarrantOrRight, resolveWtRtLimit, WT_RT_LIMIT_SOURCE } from "../utils/spacThresholds";
import { exactExposure } from "../utils/hoverPrecision";
import HoverValue from "./HoverValue";
import { addGreeks, aggregateByInvestmentType, investmentTypeKey, investmentTypeLabel, isSpacStrategyType } from "../utils/investmentTypeGrouping";
import PositionDetailsTable, { ALL_EXPOSURES } from "./PositionDetailsTable";
import StrategyTypeRiskAnalytics from "./StrategyTypeRiskAnalytics";
import StrategyTypeRiskTrends from "./StrategyTypeRiskTrends";

function aggregateByStrategy(rows) {
  const map = new Map();
  for (const row of rows) {
    const existing = map.get(row.strategy_name) || { strategy_name: row.strategy_name, lmv: 0, smv: 0, gross_exposure: 0, position_count: 0 };
    existing.lmv += row.lmv;
    existing.smv += row.smv;
    existing.gross_exposure += row.gross_exposure;
    existing.position_count += 1;
    if (row.var_95 != null) existing.var_95 = (existing.var_95 ?? 0) + row.var_95;
    if (row.var_99 != null) existing.var_99 = (existing.var_99 ?? 0) + row.var_99;
    addGreeks(existing, row);
    map.set(row.strategy_name, existing);
  }
  return [...map.values()];
}

// The issuer lookup falls back to "Unknown" for investments with no issuer match; those rows are not shown.
export const withoutUnknownIssuers = (rows) => rows.filter((r) => String(r.issuer_name ?? "").trim().toLowerCase() !== "unknown");

export function aggregateByIssuer(rows) {
  const map = new Map();
  for (const row of rows) {
    const existing = map.get(row.issuer_name) || { issuer_name: row.issuer_name, lmv: 0, smv: 0, gross_exposure: 0, position_count: 0 };
    existing.lmv += row.lmv;
    existing.smv += row.smv;
    existing.gross_exposure += row.gross_exposure;
    existing.position_count += 1;
    // VaR per investment_code is each investment's P&L in the strategy type's VaR scenario, so it is additive:
    // an issuer's VaR is the sum over its investment codes (the sum of all issuers is the type's VaR).
    if (row.var_95 != null) existing.var_95 = (existing.var_95 ?? 0) + row.var_95;
    if (row.var_99 != null) existing.var_99 = (existing.var_99 ?? 0) + row.var_99;
    map.set(row.issuer_name, existing);
  }
  return [...map.values()];
}

const TAB_POSITION_DETAILS = 0;
const TAB_RISK_ANALYTICS = 1;
const TAB_RISK_TRENDS = 2;

/**
 * Drill-down: strategy type -> strategy -> investment code, backed by
 * riskLimitsApi.getUtilizationDetail (data360's own live exposure, not
 * RiskLimits_Backend's simulation-derived limits dataset). Two levels only —
 * data360 doesn't carry RiskLimits' per-fund (GCEMF/GCM/GIP) position-size
 * breakdown, so that portfolio picker isn't ported here.
 *
 * Two tabs: Position Details (the original table) and Exposure Analytics (a
 * strategy-level donut/ranking/bar-chart trio — see
 * StrategyTypeRiskAnalytics — answering "which strategy inside this type is
 * driving its risk?").
 */
export default function StrategyTypeDrillDownModal({ open, onClose, strategyTypeName, asOfDate, riskCapital, limitCaps = {}, drivingMeasure = "GMV" }) {
  const [tab, setTab] = useState(TAB_POSITION_DETAILS);
  // Exposure columns shown on Position Details; defaults to the strategy type's Driving Measure
  // from Thresholds Admin and resets whenever that (or the strategy type) changes.
  const [visibleExposures, setVisibleExposures] = useState([drivingMeasure]);
  useEffect(() => {
    setVisibleExposures([drivingMeasure]);
  }, [strategyTypeName, drivingMeasure]);
  const toggleExposure = (measure) =>
    setVisibleExposures((prev) => {
      if (!prev.includes(measure)) return ALL_EXPOSURES.filter((m) => m === measure || prev.includes(m));
      return prev.length > 1 ? prev.filter((m) => m !== measure) : prev; // keep at least one
    });
  // SPACs add a first level: investment type (Unit/Right/Warrant/Equity) -> strategy -> investment code.
  const isSpac = isSpacStrategyType(strategyTypeName);
  const [selectedInvestmentType, setSelectedInvestmentType] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [selectedViewMode, setViewMode] = useState("strategy");
  // The issuer view has no investment-type breakdown, so SPACs always use the strategy view.
  const viewMode = isSpac ? "strategy" : selectedViewMode;
  let level = "strategies";
  if (isSpac && selectedInvestmentType === null) level = "investmentTypes";
  else if (selectedStrategy !== null) level = "investments";
  const [sortField, setSortField] = useState("gross_exposure");
  const [sortDir, setSortDir] = useState("desc");

  const { data: detailRows = [], isLoading } = useRiskLimitsUtilizationDetail(strategyTypeName, asOfDate, open);
  const { data: strategyVar, isError: varError } = useRiskLimitsStrategyVar(strategyTypeName, asOfDate, open);
  const { data: investmentVar, isError: investmentVarError } = useRiskLimitsInvestmentVar(
    strategyTypeName,
    asOfDate,
    open,
  );

  const { data: greeksDetail } = useRiskLimitsGreeksDetail(strategyTypeName, asOfDate, open);
  const greekScenarios = useMemo(() => (greeksDetail?.scenarios || []).map((s) => s.scenario), [greeksDetail]);

  // Warrants/rights are held to their own LMV/RC limit (default 2.18%), or - via the toggle -
  // to the PM approval level the backend derives from the day's AUM and the SPAC risk capital.
  const [wtRtSource, setWtRtSource] = useState(WT_RT_LIMIT_SOURCE.STANDARD);
  const { data: pmApproval } = useRiskLimitsPmApproval(asOfDate, open && isSpac);
  const pmApprovalLevelPct = pmApproval?.pm_approval_level_pct ?? null;
  const wtRtLimit = resolveWtRtLimit({
    source: wtRtSource,
    wtRtLimitPct: limitCaps.max_wt_rt_lmv_pct,
    pmApprovalLevelPct,
  });
  const isWtRtView = isSpac && isWarrantOrRight(selectedInvestmentType);
  const pctText = (v) => (v == null ? "\u2014" : formatPercent(v, 2));

  // Fetch issuer-level data
  const { data: issuerRows = [] } = useQuery(
    ["riskLimits", "issuer-detail", strategyTypeName, asOfDate],
    async () => {
      const response = await riskLimitsApi.getIssuerDetail({
        start_date: asOfDate,
        end_date: asOfDate,
        strategy_type_name: strategyTypeName,
      });
      return withoutUnknownIssuers(response?.data || []);
    },
    {
      enabled: Boolean(open && viewMode === "issuer"),
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  // Greeks P&L per strategy name / per (strategy, investment code) from Risk Monitor.
  const greeksByStrategy = useMemo(
    () => new Map((greeksDetail?.strategies || []).map((r) => [r.strategy_name, r.greeks])),
    [greeksDetail],
  );
  const greeksByInvestment = useMemo(
    () => new Map((greeksDetail?.investments || []).map((r) => [`${r.strategy_name}\u0000${r.investment_code}`, r.greeks])),
    [greeksDetail],
  );
  const withInvestmentGreeks = (row) => ({ ...row, greeks: greeksByInvestment.get(`${row.strategy_name}\u0000${row.investment_code}`) });

  // VaR is joined by strategy_name; a strategy Risk Monitor didn't simulate keeps null VaR.
  const strategiesData = useMemo(() => {
    const varByName = new Map((strategyVar?.rows || []).map((r) => [r.strategy_name, r]));
    return aggregateByStrategy(detailRows).map((row) => ({
      ...row,
      var_95: varByName.get(row.strategy_name)?.var_95 ?? null,
      var_99: varByName.get(row.strategy_name)?.var_99 ?? null,
      greeks: greeksByStrategy.get(row.strategy_name),
    }));
  }, [detailRows, strategyVar, greeksByStrategy]);
  // Issuer rows are per issuer + investment_code: attach each investment's VaR so issuers (and their investments) carry it.
  const issuerRowsWithVar = useMemo(() => {
    const varByCode = new Map((investmentVar?.rows || []).map((r) => [r.investment_code, r]));
    return issuerRows.map((row) => ({
      ...row,
      var_95: varByCode.get(row.investment_code)?.var_95 ?? null,
      var_99: varByCode.get(row.investment_code)?.var_99 ?? null,
    }));
  }, [issuerRows, investmentVar]);
  const issuersData = useMemo(() => aggregateByIssuer(issuerRowsWithVar), [issuerRowsWithVar]);

  // Per-investment VaR (P&L in the type's VaR scenario) and greeks attached to each row, so any
  // grouping (investment type, or strategy within a type) is a plain sum.
  const positionRows = useMemo(() => {
    const varByCode = new Map((investmentVar?.rows || []).map((r) => [r.investment_code, r]));
    return detailRows.map((row) => ({
      ...withInvestmentGreeks(row),
      var_95: varByCode.get(row.investment_code)?.var_95 ?? null,
      var_99: varByCode.get(row.investment_code)?.var_99 ?? null,
    }));
  }, [detailRows, investmentVar, greeksByInvestment]);
  const spacRows = useMemo(() => (isSpac ? positionRows : []), [isSpac, positionRows]);
  const investmentTypesData = useMemo(() => aggregateByInvestmentType(spacRows), [spacRows]);
  const spacTypeRows = useMemo(
    () => spacRows.filter((r) => investmentTypeKey(r) === selectedInvestmentType),
    [spacRows, selectedInvestmentType],
  );
  const spacStrategiesData = useMemo(() => aggregateByStrategy(spacTypeRows), [spacTypeRows]);
  // selectedStrategy holds the clicked strategy name, or issuer name in issuer view.
  const investmentsData = useMemo(() => {
    if (viewMode === "issuer") return issuerRowsWithVar.filter((r) => r.issuer_name === selectedStrategy);
    if (isSpac) return spacTypeRows.filter((r) => r.strategy_name === selectedStrategy);
    return positionRows.filter((r) => r.strategy_name === selectedStrategy);
  }, [positionRows, issuerRowsWithVar, spacTypeRows, selectedStrategy, viewMode, isSpac]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  let tableRows;
  if (level === "investmentTypes") tableRows = investmentTypesData;
  else if (level === "strategies") {
    if (isSpac) tableRows = spacStrategiesData;
    else tableRows = viewMode === "issuer" ? issuersData : strategiesData;
  } else tableRows = investmentsData;

  // Helper to get sort value for computed fields
  const getSortValue = (row, field) => {
    if (field === "nmv") return row.lmv - row.smv;
    if (field === "lmv_pct") return riskCapital ? (Math.abs(row.lmv) / riskCapital) * 100 : 0;
    if (field === "smv_pct") return riskCapital ? (Math.abs(row.smv) / riskCapital) * 100 : 0;
    if (field === "gmv_pct") return riskCapital ? (Math.abs(row.gross_exposure) / riskCapital) * 100 : 0;
    if (field === "nmv_pct") return riskCapital ? (Math.abs(row.lmv - row.smv) / riskCapital) * 100 : 0;
    if (field.startsWith("greek:")) return Number(row.greeks?.[field.slice(6)]) || 0;
    return Number(row[field]) || 0;
  };

  const sortedRows = useMemo(() => {
    const copy = [...tableRows];
    copy.sort((a, b) => {
      const av = getSortValue(a, sortField);
      const bv = getSortValue(b, sortField);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return copy;
  }, [tableRows, sortField, sortDir, riskCapital]);

  const handleStrategyClick = (strategyName) => {
    setSelectedStrategy(strategyName);
  };

  // One step up: investment -> strategy -> (SPAC) investment type.
  const handleBack = () => {
    if (selectedStrategy !== null) setSelectedStrategy(null);
    else setSelectedInvestmentType(null);
  };

  const backLabel =
    level === "investments" ? "Back to strategies" : level === "strategies" && isSpac ? "Back to investment types" : null;

  const handleClose = () => {
    setSelectedStrategy(null);
    setSelectedInvestmentType(null);
    setTab(TAB_POSITION_DETAILS);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      // Above the app bar (as the previous Dialog was), so the title row and the X are never covered.
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: { xs: "100vw", md: "82vw" }, maxWidth: 1500 }, "aria-label": `${strategyTypeName} details` }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1 }}>
          <Typography variant="h6">
            {strategyTypeName}
            {isSpac && selectedInvestmentType !== null && ` → ${investmentTypeLabel(selectedInvestmentType)}`}
            {selectedStrategy && ` → ${selectedStrategy}`}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Risk Capital: <HoverValue title={exactExposure(riskCapital)}>{riskCapital == null ? "—" : formatExposure(riskCapital)}</HoverValue>
            </Typography>
            <IconButton onClick={handleClose} size="small" aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        {tab === TAB_POSITION_DETAILS && level === "strategies" && !isSpac && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Typography variant="body2" sx={{ fontSize: "0.9rem" }}>View by:</Typography>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => {
                if (newMode !== null) setViewMode(newMode);
              }}
              size="small"
            >
              <ToggleButton value="strategy" aria-label="strategy view">
                <Typography variant="caption" sx={{ fontSize: "0.85rem" }}>Strategy Name</Typography>
              </ToggleButton>
              <ToggleButton value="issuer" aria-label="issuer view">
                <Typography variant="caption" sx={{ fontSize: "0.85rem" }}>Issuer</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}
        {tab === TAB_POSITION_DETAILS && !isSpac && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ fontSize: "0.9rem" }}>Exposures:</Typography>
            {ALL_EXPOSURES.map((measure) => (
              <FormControlLabel
                key={measure}
                label={measure}
                control={<Checkbox size="small" checked={visibleExposures.includes(measure)} onChange={() => toggleExposure(measure)} />}
              />
            ))}
          </Box>
        )}
        {tab === TAB_POSITION_DETAILS && isSpac && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ fontSize: "0.9rem" }}>Compare LMV/RC against:</Typography>
            <ToggleButtonGroup
              value={wtRtLimit.source}
              exclusive
              onChange={(e, next) => {
                if (next !== null) setWtRtSource(next);
              }}
              size="small"
            >
              <ToggleButton value={WT_RT_LIMIT_SOURCE.STANDARD} aria-label="warrant and right limit">
                <Typography variant="caption" sx={{ fontSize: "0.85rem" }}>WT/RT limit ({pctText(limitCaps.max_wt_rt_lmv_pct)})</Typography>
              </ToggleButton>
              <ToggleButton value={WT_RT_LIMIT_SOURCE.PM_APPROVAL} disabled={pmApprovalLevelPct == null} aria-label="PM approval level">
                <Typography variant="caption" sx={{ fontSize: "0.85rem" }}>PM approval level ({pctText(pmApprovalLevelPct)})</Typography>
              </ToggleButton>
            </ToggleButtonGroup>
            {pmApprovalLevelPct == null && (
              <Typography variant="caption" color="text.secondary">
                No PM approval level set - choose one in Threshold Admin (SPAC PM Approval).
              </Typography>
            )}
          </Box>
        )}
        <Tabs value={tab} onChange={(e, next) => setTab(next)} sx={{ mt: 1 }}>
          <Tab label="Position Details" />
          <Tab label="Exposure Analytics" />
          <Tab label="Risk Analytics" />
        </Tabs>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 600 }}>
        {tab === TAB_RISK_TRENDS ? (
          <StrategyTypeRiskTrends strategyTypeName={strategyTypeName} asOfDate={asOfDate} enabled={open} />
        ) : isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : tab === TAB_RISK_ANALYTICS ? (
          <StrategyTypeRiskAnalytics
            strategiesData={strategiesData}
            riskCapital={riskCapital}
            limitCaps={limitCaps}
            strategyTypeName={strategyTypeName}
          />
        ) : (
          <PositionDetailsTable
            level={level}
            selectedStrategy={selectedStrategy}
            viewMode={viewMode}
            rows={sortedRows}
            sortField={sortField}
            sortDir={sortDir}
            onSort={handleSort}
            onStrategyClick={handleStrategyClick}
            onInvestmentTypeClick={setSelectedInvestmentType}
            onBack={handleBack}
            backLabel={backLabel}
            lmvOnly={isSpac}
            wtRtLimitPct={wtRtLimit.pct}
            wtRtLimitLabel={wtRtLimit.label}
            investmentTypeCode={selectedInvestmentType}
            riskCapital={riskCapital}
            limitCaps={limitCaps}
            varAsOf={isSpac || viewMode === "issuer" ? investmentVar?.valuationDate : strategyVar?.valuationDate}
            varUnavailable={isSpac || viewMode === "issuer" ? investmentVarError : varError}
            greekScenarios={greekScenarios}
            visibleExposures={visibleExposures}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Drawer>
  );
}
